/**
 * Participant Form Component
 * Form for joining a Secret Santa event
 */

import { useState } from 'react'
import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import { ParticipantFormData } from '@/services/secretSanta.service'
import { useAuth } from '@/contexts/AuthContext'

interface ParticipantFormProps {
  onSubmit: (data: ParticipantFormData) => Promise<void>
  onCancel: () => void
}

export const ParticipantForm = ({ onSubmit, onCancel }: ParticipantFormProps) => {
  const { user } = useAuth()
  const [formData, setFormData] = useState<ParticipantFormData>({
    name: user?.displayName || '',
    email: user?.email || '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!formData.name.trim()) {
      setError('Name is required')
      return
    }

    if (!formData.email.trim() || !formData.email.includes('@')) {
      setError('Valid email is required')
      return
    }

    setIsSubmitting(true)
    try {
      await onSubmit(formData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join event')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Name */}
      <Input
        id="name"
        label="Your Name"
        type="text"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        placeholder="John Doe"
        required
      />

      {/* Email */}
      <Input
        id="email"
        label="Your Email"
        type="email"
        value={formData.email}
        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        placeholder="john@example.com"
        required
      />

      {/* Buttons */}
      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Joining...' : 'Join Event'}
        </Button>
      </div>
    </form>
  )
}
